import { useState, useEffect, useCallback, useMemo } from "react"
import { useUrlSigner } from "./useUrlSigner"
import { FetchState } from "../Models/Client/FetchState"
import { Result } from "../Models/Utility/Result"
import { asError } from "../Helpers/Functions/error"
import { dereference, hasReferenceData } from "universal-schema/lib"
import { DataAccess } from "../Models/Client/DataAccess"

export function useDereference(
    data: unknown,
    dataAccess: DataAccess
): FetchState<unknown> {
    const [state, setState] = useState<{
        data?: unknown
        result?: FetchState<unknown>
    }>({})

    const signer = useUrlSigner(dataAccess)
    const fetchData = useCallback(
        async (url: string): Promise<unknown> => {
            const signedUrl = await signer(url)
            const res = await fetch(signedUrl)

            if (res.status >= 300) {
                throw Error(
                    `Failed to fetch referenced data from storage. Status: ${res.status}`
                )
            }

            return res.json()
        },
        [signer]
    )

    const shouldDereferenceData = useMemo(() => hasReferenceData(data), [data])
    useEffect(() => {
        if (state.data !== undefined || data === undefined) {
            return
        }

        if (shouldDereferenceData) {
            setState({ data, result: "loading" })
            dereference(data, fetchData)
                .then(dereferencedData =>
                    setState({ data, result: Result.ok(dereferencedData) })
                )
                .catch(e =>
                    setState({ data, result: Result.error(asError(e).message) })
                )
        } else {
            setState({
                data,
                result:
                    data === undefined || data === null
                        ? undefined
                        : Result.ok(data)
            })
        }
    }, [data, fetchData, state, shouldDereferenceData])

    // reset value on dependent data change
    useEffect(() => {
        setState({})
    }, [data])

    return useMemo(
        () => (state.data === data ? state.result : undefined),
        [state, data]
    )
}
